import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useLocation, useNavigate } from "react-router-dom";
import DashboardShell from "@/layouts/ProviderDashboardLayout";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Standalone accept-invitation page. If the invitee is signed in they can
 * accept immediately; otherwise we point them at login/register with the token
 * preserved in the URL so we can complete the accept post-auth.
 */
export default function TeamAcceptPage() {
  const { user } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const params = new URLSearchParams(loc.search);
  const token = params.get("token") || "";
  const email = params.get("e") || "";
  const [state, setState] = useState("idle"); // idle | accepting | done | error
  const [error, setError] = useState(null);

  const doAccept = async () => {
    setState("accepting");
    try {
      const { data } = await api.post("/team/invitations/accept", { token });
      setState("done");
      toast.success("You're now on the team.");
      setTimeout(() => nav("/dashboard"), 1500);
    } catch (err) {
      setState("error");
      setError(err?.response?.data?.detail || err?.response?.data?.error || "Could not accept");
    }
  };

  return (
    <DashboardShell>
      <div className="max-w-lg mx-auto" data-testid="team-accept">
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center">
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Team invitation</div>
          <div className="mt-2 font-display text-2xl font-semibold text-[#071b22]">Join a Gulf Coast Work team</div>
          {!token && <div className="mt-3 text-sm text-rose-600">Missing token — check the link you were sent.</div>}
          {token && !user && (
            <>
              <div className="mt-3 text-sm text-[#252A34]/70">You need to sign in{email ? ` as ${email}` : ""} to accept this invitation.</div>
              <button onClick={() => nav(`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`)} className="mt-4 rounded-full bg-[#165a71] text-white px-5 py-2 text-sm font-medium" data-testid="team-accept-signin">Sign in</button>
            </>
          )}
          {token && user && state === "idle" && (
            <>
              <div className="mt-3 text-sm text-[#252A34]/70">You're signed in as <b>{user.email}</b>. Ready to accept?</div>
              <button onClick={doAccept} className="mt-4 rounded-full bg-[#165a71] text-white px-5 py-2 text-sm font-medium" data-testid="team-accept-confirm">Accept invitation</button>
            </>
          )}
          {state === "accepting" && <div className="mt-3 text-sm text-[#252A34]/70">Accepting…</div>}
          {state === "done" && <div className="mt-3 text-sm text-emerald-700">You're in! Redirecting…</div>}
          {state === "error" && <div className="mt-3 text-sm text-rose-600">{error}</div>}
        </div>
      </div>
    </DashboardShell>
  );
}
